import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Header from '../../components/common/Header';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import Button from '../../components/common/Button';
import MessagePage from '../MessagePage';
import NotFound from '../NotFound';
import { apiService } from '../../services/api';
import { Competition } from '../../types/interfaces';
import { ROLE_LABELS, errorStatus } from '../../services/manageApi';
import { usePermissions } from './usePermissions';
import { MANAGE_TABS, ManageTabId } from './manageTabs';
import OverviewTab from './OverviewTab';
import ContentTab from './ContentTab';
import EvaluationTab from './EvaluationTab';
import PeopleTab from './PeopleTab';
import { formatDate } from '../../utils/dates';
import '../ManageCompetition.css';
import './ManageTabs.css';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const UNSAVED_PROMPT = 'You have unsaved changes on this tab. Leave without saving?';

type LoadError = { type: 'not-found' | 'private' | 'unauthorized'; message: string } | null;

/**
 * /manage/:id — competition management for platform admins and hosts.
 * Which fields are editable comes from GET /competitions/:id/my_permissions.
 */
const ManageCompetitionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const competitionId = Number(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    permissions, loading: permissionsLoading, failureStatus, canEdit, can,
  } = usePermissions(competitionId);
  const [competition, setCompetition] = useState<Competition | null>(null);
  useDocumentTitle(competition?.title ? `Manage · ${competition.title}` : undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<LoadError>(null);
  const [dirtySections, setDirtySections] = useState<Record<string, boolean>>({});

  const requestedTab = searchParams.get('tab');
  const activeTab: ManageTabId = MANAGE_TABS.some((tab) => tab.id === requestedTab)
    ? (requestedTab as ManageTabId)
    : 'overview';
  // Any change of tab (in-app click or browser Back/Forward on ?tab=) unmounts the
  // old tab's sections, so their dirty flags no longer apply. Reset during render
  // (not in an effect) so the new tab's sections can report dirty on mount.
  const [trackedTab, setTrackedTab] = useState<ManageTabId>(activeTab);
  if (trackedTab !== activeTab) {
    setTrackedTab(activeTab);
    setDirtySections({});
  }
  const hasUnsavedChanges = Object.values(dirtySections).some(Boolean);

  useEffect(() => {
    if (!Number.isFinite(competitionId)) return undefined;
    let cancelled = false;
    setLoading(true);
    apiService.post(`/competitions/${competitionId}`, {})
      .then((res: { data: Competition }) => {
        if (cancelled) return;
        setCompetition(res.data);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = errorStatus(err);
        if (status === 404) setLoadError({ type: 'not-found', message: 'Competition not found' });
        else if (status === 403) setLoadError({ type: 'private', message: 'This competition is private' });
        else if (status === 401) setLoadError({ type: 'unauthorized', message: 'You need to be logged in' });
        else setLoadError({ type: 'not-found', message: 'Failed to load competition' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const markDirty = useCallback((section: string, dirty: boolean) => {
    setDirtySections((prev) => (Boolean(prev[section]) === dirty ? prev : { ...prev, [section]: dirty }));
  }, []);

  const selectTab = useCallback((tab: ManageTabId) => {
    if (tab === activeTab) return;
    if (hasUnsavedChanges && !window.confirm(UNSAVED_PROMPT)) return;
    setSearchParams({ tab });
  }, [activeTab, hasUnsavedChanges, setSearchParams]);

  /** True when it is safe to leave this tab: nothing unsaved, or the user agreed to drop it. */
  const confirmLeave = useCallback(
    () => !hasUnsavedChanges || window.confirm(UNSAVED_PROMPT),
    [hasUnsavedChanges],
  );

  if (loading || permissionsLoading) {
    return (
      <>
        <Header />
        <div className="manage-competition-page manage-competition-page--loading">
          <LoadingSpinner text="Loading competition management..." />
        </div>
      </>
    );
  }

  if (loadError) {
    return <NotFound errorType={loadError.type} message={loadError.message} />;
  }

  const permissionsDenied = failureStatus === 401 || failureStatus === 403;
  if (!permissions && failureStatus !== null && !permissionsDenied) {
    return (
      <>
        <Header />
        <div className="manage-competition-page">
          <p className="manage-error" role="alert">
            Couldn&apos;t load your permissions for this competition.{' '}
            <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>Retry</Button>
          </p>
        </div>
      </>
    );
  }

  if (!permissions || !competition) {
    return (
      <MessagePage
        title="Access Denied"
        message="You do not have permission to view this page."
        type="error"
        showCloseButton={true}
        autoClose={8}
      />
    );
  }

  return (
    <>
      <Header />
      <div className="manage-competition-page">
        <Breadcrumbs
          className="manage-competition-breadcrumbs"
          items={[
            { label: 'Dashboard', to: '/dashboard' },
            { label: 'Competition', to: `/competition/${competition.id}` },
            { label: 'Manage' },
          ]}
        />

        <div className="manage-competition-header" data-tour="manage-summary">
          {competition.logo_path && (
            <img src={competition.logo_path} alt={`${competition.title} logo`} className="manage-competition-logo" />
          )}
          <div className="manage-competition-header__text">
            <h1 style={{ marginBottom: '0.25rem' }}>{competition.title}</h1>
            <div className="manage-competition-subtitle">
              <span className="manage-competition-pill">Status: {competition.status}</span>
              {competition.type && <span className="manage-competition-pill">Type: {competition.type}</span>}
              <span className="manage-competition-pill">Mode: {competition.competition_mode || 'individual'}</span>
              <span className="manage-competition-pill">
                {formatDate(competition.start_date, '—')} – {formatDate(competition.end_date, '—')}
              </span>
              <span className="manage-competition-pill manage-competition-pill--admin">You: {ROLE_LABELS[permissions.role]}</span>
            </div>
          </div>
        </div>

        <div className="manage-tabs">
          <div className="manage-tabs__list" role="tablist" aria-label="Manage competition" data-tour="manage-tabs">
            {MANAGE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`manage-tab-${tab.id}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`manage-panel-${tab.id}`}
                className={`manage-tabs__tab${activeTab === tab.id ? ' manage-tabs__tab--active' : ''}`}
                onClick={() => selectTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {can('present') && (
            // eslint-disable-next-line react/jsx-no-target-blank -- same-origin link; the opener is needed (see rel below)
            <a
              className="manage-tabs__present"
              data-tour="manage-present"
              href={`/manage/${competition.id}/present`}
              target="_blank"
              // Same-origin page: keep the opener so the browser copies
              // sessionStorage (where password logins keep the token).
              rel="opener"
            >
              Present ▶
            </a>
          )}
        </div>

        <div
          role="tabpanel"
          id={`manage-panel-${activeTab}`}
          aria-labelledby={`manage-tab-${activeTab}`}
          className="manage-tabs__panel"
        >
          {activeTab === 'overview' && (
            <OverviewTab
              competition={competition}
              shortcuts={MANAGE_TABS.filter((tab) => tab.id !== 'overview')}
              onOpenTab={selectTab}
            />
          )}
          {activeTab === 'content' && (
            <ContentTab
              competition={competition}
              permissions={permissions}
              canEdit={canEdit}
              onCompetitionUpdated={setCompetition}
              onDirtyChange={markDirty}
            />
          )}
          {activeTab === 'evaluation' && (
            <EvaluationTab
              competition={competition}
              permissions={permissions}
              canEdit={canEdit}
              onCompetitionUpdated={setCompetition}
              onDirtyChange={markDirty}
              confirmLeave={confirmLeave}
            />
          )}
          {activeTab === 'people' && <PeopleTab competition={competition} role={permissions.role} />}
        </div>
      </div>
    </>
  );
};

export default ManageCompetitionPage;
